import { Component, inject, viewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { RideStatusType } from '../../../../../shared/enums/rideStatusType';
import { RequestBuilderService } from '../../../../../shared/services/request-builder.service';
import { StateService } from '../../../../../shared/services/state.service';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [FormsModule, ButtonComponent, InputTextModule, TextareaModule],
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.scss',
})
export class ConfirmDialogComponent {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly messageService = inject(MessageService);

  private readonly stateService = inject(StateService);

  readonly reasonInput = viewChild<NgModel>('reasonInput');
  loading = false;
  reason: string = '';

  confirmAction() {
    if (this.reasonInput()?.invalid) return;

    // Prepare base request body
    const body = {
      ...(this.config.data.hasInput && { internalNote: this.reason }),
    };

    this.loading = true;

    // Handle specific action types
    switch (this.config.data.action) {
      case 'ignoreDriverDoubleBooked':
      case 'ignoreMonitorDoubleBooked':
        this.handleIgnoreDoubleBooking(body);
        break;
      case 'autoAcceptRide':
        this.handleAutoAcceptRide(body);
        break;
      case 'sendRideOffer':
        this.handleGenericAction(body, `send-offer/manual`);
        break;
      case 'completeRide':
        this.handleGenericAction(body, `complete`);
        break;
      case 'unAssignDriver':
        this.handleGenericAction(body, `driver/unassigns`);
        break;
      case 'autoStartRide':
        this.handleAutoStartRide(body);
        break;
      default:
        this.loading = false;
        break;
    }
  }

  private handleIgnoreDoubleBooking(body: any) {
    const type = this.config.data.action === 'ignoreDriverDoubleBooked' ? 'driver' : 'monitor';
    const endpoint = `rides/${this.config.data.uuid}/ignore-double-booking?type=${type}`;

    this.requestBuilder.put(endpoint, body).subscribe({
      next: () => this.handleSuccess(),
      error: error => this.handleError(error),
    });
  }

  private handleAutoAcceptRide(body: any) {
    const updatedBody = {
      ...body,
      isAutomatic: false,
      rideStatusId: RideStatusType.Accepted,
    };

    const endpoint = `jobs/job-offers/${this.config.data.uuid}/status`;

    this.requestBuilder.patch(endpoint, updatedBody).subscribe({
      next: () => this.handleSuccess(),
      error: error => this.handleError(error),
    });
  }

  private handleAutoStartRide(body: any) {
    const updatedBody = {
      ...body,
      rideStatusId: RideStatusType.InProgress,
    };

    this.handleGenericAction(updatedBody, 'status');
  }

  private handleGenericAction(body: any, actionPath: string) {
    const endpoint = `rides/${this.config.data.uuid}/${actionPath}`;
    const isUnassignDriver = actionPath === 'driver/unassigns';

    this.requestBuilder.patch(endpoint, body).subscribe({
      next: () => this.handleSuccess(isUnassignDriver),
      error: error => this.handleError(error),
    });
  }

  private handleSuccess(reorderToTop = false) {
    this.loading = false;
    if (reorderToTop && this.config.data.id) {
      this.stateService.setReorderRideToTop(Number(this.config.data.id));
    }
    this.ref.close({ confirmed: true });
  }

  private handleError(err: any) {
    this.loading = false;
    console.log(err);
    if (err.error?.message) {
      this.showError(err.error.message);
    }
  }

  showError(errorMessage: string) {
    this.messageService.add({
      severity: 'danger',
      summary: 'Something went wrong!',
      detail: errorMessage,
      life: 6000,
    });
  }
}
